/**
 * DOM HUD.
 *
 * Rules kept:
 * - It reads one state object and writes DOM only when a value actually changed, at most 4 times per
 *   second (`HUD_INTERVAL_MS`), so no DOM work happens inside the render path or per frame.
 * - Every numeric cell uses `font-variant-numeric: tabular-nums` (see `index.html`) so values cannot
 *   shift the layout while they change.
 * - It never touches simulation or world state, and its listener count is fixed: one click handler on
 *   the retry button, added once and removed in `dispose()`.
 * - `setNotice()` is the only out-of-band write and the only non-fatal server surface: a notice is
 *   appended to the already-throttled diagnostic line (written once immediately, then kept current by
 *   `update()`), never an overlay, because the overlay can only be cleared by the next `welcome`.
 * - All markup is created here (no CDN, no external font); only class names come from `index.html`.
 */

import type { BlockId, Vec3 } from "@rogueworlds/shared";
import { BLOCKS } from "./world/bridge";

export type HudStatus = "connecting" | "connected" | "disconnected" | "error";

export interface HudState {
  readonly fps: number;
  readonly ping: number;
  readonly serverTick: number;
  readonly players: number;
  readonly position: Vec3;
  readonly chunks: number;
  readonly meshed: number;
  readonly pendingEdits: number;
  readonly mesherQueue: number;
  readonly mesherInFlight: number;
  readonly mesherWorkers: number;
  readonly tier: string;
  readonly transport: string;
  readonly status: HudStatus;
  readonly correction: number;
  readonly locked: boolean;
  readonly selectedBlock: BlockId;
}

export interface OverlayState {
  readonly title: string;
  readonly detail: string;
  readonly error: boolean;
  readonly retry: boolean;
}

/** HUD refresh interval: 4 Hz, per the runtime contract. */
const HUD_INTERVAL_MS = 250;

const ROWS: readonly (readonly [string, string])[] = [
  ["fps", "fps"],
  ["ping", "ping"],
  ["tick", "tick"],
  ["players", "players"],
  ["pos", "pos"],
  ["chunks", "chunks"],
  ["edits", "edits"],
  ["queue", "queue"],
  ["net", "net"],
] as const;

/** Hotbar swatch colours come from the voxel registry, so the HUD cannot drift from the world. */
function blockColor(block: BlockId): string {
  const def = BLOCKS[block];
  if (def === undefined) {
    return `hsl(${(block * 47) % 360} 35% 45%)`;
  }
  const [r, g, b] = def.color;
  return `rgb(${Math.round(r * 255)} ${Math.round(g * 255)} ${Math.round(b * 255)})`;
}

function blockName(block: BlockId): string {
  const def = BLOCKS[block];
  return def === undefined ? `block ${block}` : def.name;
}

export class Hud {
  private readonly root: HTMLElement;
  private readonly values = new Map<string, HTMLElement>();
  private readonly lastWritten = new Map<string, string>();
  private readonly diagnostic: HTMLElement;
  private readonly hotbar: HTMLElement;
  private readonly overlay: HTMLElement;
  private readonly overlayTitle: HTMLElement;
  private readonly overlayDetail: HTMLElement;
  private readonly retryButton: HTMLButtonElement;
  private readonly onRetry: () => void;
  private lastUpdateMs = Number.NEGATIVE_INFINITY;
  private overlayVisible = false;
  private hotbarSignature = "";
  private diagnosticBase = "";
  private notice = "";
  private disposed = false;

  private readonly onRetryClick = (): void => {
    this.onRetry();
  };

  constructor(root: HTMLElement, onRetry: () => void) {
    this.root = root;
    this.onRetry = onRetry;

    const panel = element("div", "hud");
    for (const [key, label] of ROWS) {
      const row = element("div", "row");
      const name = element("span", "k");
      name.textContent = label;
      const value = element("span", "v");
      value.textContent = "-";
      row.append(name, value);
      panel.append(row);
      this.values.set(key, value);
    }
    this.diagnostic = element("div", "diag");
    this.diagnostic.textContent = "-";
    panel.append(this.diagnostic);

    const crosshair = element("div", "crosshair");
    const hint = element("div", "hint");
    hint.append(
      text("Click to capture the mouse"),
      lineBreak(),
      text("WASD move / Space jump / Shift sprint"),
      lineBreak(),
      text("Left click break / Right click place / 1-9 select block"),
      lineBreak(),
      text("Esc releases the mouse"),
    );

    this.hotbar = element("div", "hotbar");

    this.overlay = element("div", "overlay");
    this.overlay.hidden = true;
    this.overlayTitle = element("div", "t");
    this.overlayDetail = element("div", "d");
    this.retryButton = document.createElement("button");
    this.retryButton.type = "button";
    this.retryButton.className = "btn";
    this.retryButton.textContent = "Retry connection";
    this.retryButton.hidden = true;
    this.retryButton.addEventListener("click", this.onRetryClick);
    this.overlay.append(this.overlayTitle, this.overlayDetail, this.retryButton);

    this.root.append(panel, crosshair, hint, this.hotbar, this.overlay);
  }

  /** Dirty-flag, 4 Hz-max DOM update. Safe to call every frame. */
  update(state: HudState, nowMs: number): void {
    if (this.disposed || nowMs - this.lastUpdateMs < HUD_INTERVAL_MS) {
      return;
    }
    this.lastUpdateMs = nowMs;
    this.write("fps", state.fps.toFixed(0));
    this.write("ping", `${state.ping.toFixed(0)} ms`);
    this.write("tick", state.serverTick.toFixed(0));
    this.write("players", state.players.toFixed(0));
    this.write(
      "pos",
      `${state.position.x.toFixed(1)} ${state.position.y.toFixed(1)} ${state.position.z.toFixed(1)}`,
    );
    this.write("chunks", `${state.chunks} / ${state.meshed} meshed`);
    this.write("edits", `${state.pendingEdits} predicted (${state.correction.toFixed(2)} m corr)`);
    this.write("queue", `${state.mesherQueue} queued, ${state.mesherInFlight} meshing`);
    this.write(
      "net",
      `${state.status} via ${state.transport}${state.locked ? "" : " (mouse free)"}`,
    );
    this.diagnosticBase =
      `${state.tier} | ${state.mesherWorkers} workers | ${state.chunks} chunks | hotbar ${state.selectedBlock}`;
    this.writeDiagnostic(this.diagnosticLine());
  }

  /**
   * The HUD's one non-fatal surface: a server notice (a throttle, a refused interest key, an edit the
   * verdict repairs) that must not be presented as fatal. It is appended to the diagnostic line, so it
   * never covers the running game, and it stays until it is replaced; `null` clears it.
   */
  setNotice(text: string | null): void {
    if (this.disposed) {
      return;
    }
    const next = text ?? "";
    if (next === this.notice) {
      return;
    }
    this.notice = next;
    this.writeDiagnostic(this.diagnosticLine());
  }

  setOverlay(state: OverlayState | null): void {
    if (this.disposed) {
      return;
    }
    if (state === null) {
      if (this.overlayVisible) {
        this.overlay.hidden = true;
        this.overlayVisible = false;
      }
      return;
    }
    if (!this.overlayVisible) {
      this.overlay.hidden = false;
      this.overlayVisible = true;
    }
    this.overlay.classList.toggle("bad", state.error);
    this.overlayTitle.textContent = state.title;
    this.overlayDetail.textContent = state.detail;
    this.retryButton.hidden = !state.retry;
  }

  /** Rebuilds the hotbar only when the block list or the selection changed. */
  setHotbar(blocks: readonly BlockId[], selected: number): void {
    if (this.disposed) {
      return;
    }
    const signature = `${blocks.join(",")}|${selected}`;
    if (signature === this.hotbarSignature) {
      return;
    }
    this.hotbarSignature = signature;
    this.hotbar.replaceChildren();
    for (let i = 0; i < blocks.length; i++) {
      const block = blocks[i] ?? 0;
      const slot = element("div", i === selected ? "slot sel" : "slot");
      slot.title = blockName(block);
      const swatch = document.createElement("i");
      swatch.style.background = blockColor(block);
      const label = element("span", "n");
      label.textContent = `${i + 1}`;
      slot.append(swatch, label);
      this.hotbar.append(slot);
    }
  }

  dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    this.retryButton.removeEventListener("click", this.onRetryClick);
    this.root.replaceChildren();
    this.values.clear();
    this.lastWritten.clear();
  }

  private write(key: string, value: string): void {
    if (this.lastWritten.get(key) === value) {
      return;
    }
    this.lastWritten.set(key, value);
    const cell = this.values.get(key);
    if (cell !== undefined) {
      cell.textContent = value;
    }
  }

  private writeDiagnostic(value: string): void {
    if (this.lastWritten.get("diag") === value) {
      return;
    }
    this.lastWritten.set("diag", value);
    this.diagnostic.textContent = value;
  }

  private diagnosticLine(): string {
    const base = this.diagnosticBase;
    if (this.notice === "") {
      return base;
    }
    return base === "" ? this.notice : `${base} | ${this.notice}`;
  }
}

function element<K extends keyof HTMLElementTagNameMap>(tag: K, className: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== "") {
    node.className = className;
  }
  return node;
}

function text(value: string): Text {
  return document.createTextNode(value);
}

function lineBreak(): HTMLElement {
  return document.createElement("br");
}
